'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { FileText, Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { API_ENDPOINTS } from '@/lib/api/config';
import {
  DocumentPagesViewer,
  legacyViewerPage,
  ocrQueries,
  type ViewerPage
} from '@/features/ocr';

interface OcrSearchViewerDialogProps {
  ocrResultId: number | null;
  /** chunk_id strings whose bbox should be highlighted (blue). */
  matchedChunkIds: string[];
  /** Container number to show in the dialog title. */
  containerNumber: string | null;
  /** 1-based page number to land on. Defaults to the first matched chunk's page. */
  initialPageNumber: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Opens an OCR result inside the search page, with the chunks the user's
 * query matched highlighted. Reuses the OCR feature's DocumentPagesViewer
 * verbatim — passing matched chunk IDs as `highlightedChunkIds` gives the
 * same blue spotlight the field-driven highlights use, while leaving the
 * viewer's built-in within-document search bar available.
 */
export function OcrSearchViewerDialog({
  ocrResultId,
  matchedChunkIds,
  containerNumber,
  initialPageNumber,
  open,
  onOpenChange
}: OcrSearchViewerDialogProps) {
  const t = useTranslations('search');
  const enabled = open && ocrResultId != null;

  const { data: response, isPending } = useQuery({
    ...ocrQueries.result(ocrResultId ?? 0),
    enabled
  });
  const result = response?.data;

  const viewerPages: ViewerPage[] = useMemo(() => {
    if (!result) return [];
    // The pipeline always populates `pages`; the single-page legacy fallback
    // mirrors what ocr-results-page does so dialogs render for older results.
    // It returns null for a result that has no page row at all (an OCR run the
    // scan chain never linked to a container), leaving `viewerPages` empty so
    // the dialog shows its "no pages" state instead of a page labelled `#null`.
    const fallback = legacyViewerPage(result);
    const pages =
      result.pages && result.pages.length > 0
        ? result.pages
        : fallback
          ? [fallback]
          : [];

    return pages.map((p, index) => {
      const pageChunks = (result.chunks ?? []).filter(
        (c) => c.page_number === index + 1
      );
      // Local disk emits `image_url: null` — fall back to the authenticated
      // PAGE_IMAGE streaming endpoint when `has_image` is true so the viewer
      // works regardless of the storage backend.
      const authEndpoint =
        result.batch_id != null && (p.image_url || p.has_image)
          ? API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(result.batch_id, p.id)
          : null;
      return {
        imageUrl: authEndpoint ? null : p.image_url,
        authEndpoint,
        label: p.page_number
          ? t('pageLabelInDialog', { number: p.page_number })
          : `#${p.id}`,
        chunks: pageChunks,
        isProcessing: false
      };
    });
  }, [result, t]);

  // Convert the 1-based matched page number to a 0-based viewer page index.
  // Falls back to the first chunk's page if the parent didn't pass one in.
  const initialPageIndex = useMemo(() => {
    if (viewerPages.length === 0) return 0;
    const fromProp = initialPageNumber != null ? initialPageNumber - 1 : -1;
    if (fromProp >= 0 && fromProp < viewerPages.length) return fromProp;

    const firstMatch = (result?.chunks ?? []).find((c) =>
      matchedChunkIds.includes(c.chunk_id)
    );
    return firstMatch ? firstMatch.page_number - 1 : 0;
  }, [viewerPages.length, initialPageNumber, result, matchedChunkIds]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex h-[90vh] max-h-[90vh] flex-col gap-3 p-0 sm:max-w-5xl'>
        <DialogHeader className='border-b px-5 py-3'>
          <DialogTitle className='flex items-center gap-2 text-sm'>
            <FileText className='text-primary size-4' />
            {containerNumber
              ? t('viewerTitle', { container: containerNumber })
              : t('viewerTitleNoContainer')}
          </DialogTitle>
          <DialogDescription className='text-xs'>
            {matchedChunkIds.length > 0
              ? t('viewerMatchHint', { count: matchedChunkIds.length })
              : t('viewerNoMatchHint')}
          </DialogDescription>
        </DialogHeader>

        <div className='min-h-0 flex-1 px-2 pb-2'>
          {isPending ? (
            <div className='text-muted-foreground flex h-full items-center justify-center gap-2 text-sm'>
              <Loader2 className='size-4 animate-spin' />
              {t('loading')}
            </div>
          ) : viewerPages.length === 0 ? (
            <div className='text-muted-foreground flex h-full items-center justify-center text-sm'>
              {t('viewerNoPages')}
            </div>
          ) : (
            <DocumentPagesViewer
              pages={viewerPages}
              pdfUrl={result?.pdf_url ?? null}
              highlightedChunkIds={matchedChunkIds}
              activeChunkIds={[]}
              activePage={initialPageIndex}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
